/* eslint-disable max-lines -- maybe some URLs could be moved to related configs */
import {
  type PimProductV11,
  type UserOrderResponseWithFlagsSubOrdersInner,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { apmCaptureError } from '../apm'
import { PUBLIC_API_PREFIX, type PublicGlobalConfig } from '../global-config'
import { LOCALE } from '../i18n'
import { type CART_ACTION } from '../login-registration'
import {
  type ProductAddToCartCswCtaParams,
  type ProductAddToCartCtaParams,
} from '../product-page'
import { SupportedLanguages, TENANT } from '../tenant'

import { queryStringEncode } from './queryString'
import { urlGetPathName, urlNormalizeUri } from './url'

export const urlResolverGetHomepage = (config: PublicGlobalConfig): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: '/',
  })

export const urlResolverGetLogin = (
  config: PublicGlobalConfig,
  queryParams?: {
    [key: string]: boolean | string | undefined
    email?: string
    forceLogin?: boolean
    redirect?: string
  },
): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: `/nx/login/${queryStringEncode(queryParams)}`,
  })

export const urlResolverGetLogout = (config: PublicGlobalConfig): string =>
  urlNormalizeUri({ locale: config.locale, uri: '/logout/' })

export const urlResolverGetForgotPassword = (
  config: PublicGlobalConfig,
): string =>
  urlNormalizeUri({ locale: config.locale, uri: '/nx/forgot-password/' })

export const urlResolverGetRegister = (config: PublicGlobalConfig): string =>
  `${urlResolverGetLogin(config)}#register`

export const urlResolverGetClimateNeutralShipping = (
  config: PublicGlobalConfig,
): string =>
  urlNormalizeUri({ locale: config.locale, uri: '/klimaneutraler-versand/' })

export const urlResolverGetTrackShippingInfo = (
  config: PublicGlobalConfig,
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): string => {
  const carrierName =
    subOrder.carrier?.externalCarrierIds?.parcellab ??
    subOrder.carrier?.displayName.split('.')[1]

  const trackId = subOrder.carrier?.trackingId

  return urlNormalizeUri({
    locale: config.locale,
    uri: `/paketverfolgung/${queryStringEncode({
      courier: carrierName,
      trackingNo: trackId,
    })}`,
  })
}

// Account
export const urlResolverGetAccount = (config: PublicGlobalConfig): string =>
  urlNormalizeUri({ locale: config.locale, uri: '/nx/account/' })

export const urlResolverGetAccountOrders = (
  config: PublicGlobalConfig,
): string => `${urlResolverGetAccount(config)}orders/`

export const urlResolverGetAccountNotepad = (
  config: PublicGlobalConfig,
  source?: string,
): string =>
  source
    ? `${urlResolverGetAccount(config)}notepad/${queryStringEncode({ source })}`
    : `${urlResolverGetAccount(config)}notepad/`

export const urlResolverGetAccountRedpoints = (
  config: PublicGlobalConfig,
): string => {
  if (config.tenant === TENANT.CH) {
    return urlNormalizeUri({
      locale: config.locale,
      uri: `${urlResolverGetAccount(config)}redcoins/`,
    })
  }

  // same for other tenants
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}redpoints/`,
  })
}

export const urlResolverGetAccountOrderDetailByOrderId = (
  config: PublicGlobalConfig,
  orderId: string,
): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}orders/${orderId}`,
  })

export const urlResolverGetAccountSubOrderDetailBySubOrderId = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccountOrderDetailByOrderId(
      config,
      orderId,
    )}${subOrderId}`,
  })

export const urlResolverGetAccountSubOrderDetailBySubOrderIdMissingProducts =
  (params: {
    config: PublicGlobalConfig
    fromFragenBestellung?: boolean
    orderId: string
    subOrderId: string
  }): string => {
    const { config, fromFragenBestellung, orderId, subOrderId } = params

    return urlNormalizeUri({
      locale: config.locale,
      uri: `${urlResolverGetAccountOrderDetailByOrderId(
        config,
        orderId,
      )}${subOrderId}/missing-products${queryStringEncode({ fromFragenBestellung })}`,
    })
  }

export const urlResolverGetAccountSubOrderDetailBySubOrderIdDamagedProducts =
  (params: {
    config: PublicGlobalConfig
    fromFragenBestellung?: boolean
    orderId: string
    subOrderId: string
  }): string => {
    const { config, fromFragenBestellung, orderId, subOrderId } = params

    return urlNormalizeUri({
      locale: config.locale,
      uri: `${urlResolverGetAccountOrderDetailByOrderId(
        config,
        orderId,
      )}${subOrderId}/damaged-products${queryStringEncode({ fromFragenBestellung })}`,
    })
  }

export const urlResolverGetAccountSubOrderDetailBySubOrderIdDamagedProductsCustomerCare =
  (config: PublicGlobalConfig, orderId: string, subOrderId: string): string =>
    urlNormalizeUri({
      locale: config.locale,
      uri: `${urlResolverGetAccountOrderDetailByOrderId(
        config,
        orderId,
      )}${subOrderId}/damaged-products/customer-care`,
    })

export const urlResolverGetAccountSubOrderDetailBySubOrderIdDamagedProductsConfirmation =
  (config: PublicGlobalConfig, orderId: string, subOrderId: string): string =>
    urlNormalizeUri({
      locale: config.locale,
      uri: `${urlResolverGetAccountOrderDetailByOrderId(
        config,
        orderId,
      )}${subOrderId}/damaged-products/confirmation`,
    })

export const urlResolverGetAccountSubOrderDetailBySubOrderIdMissingProductsConfirmation =
  (config: PublicGlobalConfig, orderId: string, subOrderId: string): string =>
    urlNormalizeUri({
      locale: config.locale,
      uri: `${urlResolverGetAccountOrderDetailByOrderId(
        config,
        orderId,
      )}${subOrderId}/missing-products/confirmation`,
    })

export const urlResolverGetAccountSubOrderDetailBySubOrderIdMissingProductsCustomerCare =
  (config: PublicGlobalConfig, orderId: string, subOrderId: string): string =>
    urlNormalizeUri({
      locale: config.locale,
      uri: `${urlResolverGetAccountOrderDetailByOrderId(
        config,
        orderId,
      )}${subOrderId}/missing-products/customer-care`,
    })

export const urlResolverGetInvoiceByOrderId = (
  config: PublicGlobalConfig,
  subOrderId: string,
  documentHash: string,
): string => {
  return `${urlResolverGetAccountOrderDetailByOrderId(
    config,
    subOrderId,
  )}/document/invoice/${documentHash}`
}

export const urlResolverGetNowOrderInvoiceByOrderId = (
  config: PublicGlobalConfig,
  orderInfo: { documentId: string; orderId: string; subOrderId: string },
): string => {
  return `/homeone/api/bully/now/v3/${config.tenantAndEnv}/orders/${orderInfo.orderId}/${orderInfo.subOrderId}/documents/${orderInfo.documentId}`
}

export const urlResolverRequestMarketPlaceOrderInvoiceByOrderId = (
  config: PublicGlobalConfig,
  orderInfo: { orderId: string; subOrderId: string },
): string => {
  return `${urlResolverGetAccountSubOrderDetailBySubOrderId(
    config,
    orderInfo.orderId,
    orderInfo.subOrderId,
  )}?section=request-invoice`
}

export const urlResolverGetMarketPlaceOrderInvoiceByOrderId = (
  config: PublicGlobalConfig,
  orderInfo: { documentId: string; subOrderId: string },
): string => {
  return `${urlResolverGetAccountOrderDetailByOrderId(
    config,
    orderInfo.subOrderId,
  )}/document/${orderInfo.documentId}`
}

export const urlResolverGetSubOrderInvoicesBySubOrderId = (
  config: PublicGlobalConfig,
  subOrderId: string,
  orderId: string,
): string => {
  return `${urlResolverGetAccountSubOrderDetailBySubOrderId(
    config,
    orderId,
    subOrderId,
  )}/?section=invoice`
}

export const urlResolverGetHelpWithMyOrderBySubOrderId = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderInfo: {
    isMarketplace: boolean
    returnOrder?: boolean
    subOrderId: string
  },
): string => {
  return `${urlResolverGetAccountSubOrderDetailBySubOrderId(
    config,
    orderId,
    subOrderInfo.subOrderId,
  )}${queryStringEncode({
    section: subOrderInfo.isMarketplace ? 'orderProblem' : 'problemDialog',
    showReturnModal: subOrderInfo.returnOrder,
  })}`
}

export const urlResolverGetAccountCredit = (
  config: PublicGlobalConfig,
): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}credits`,
  })

// Cart
export const urlResolverGetCart = (
  config: PublicGlobalConfig,
  action?: CART_ACTION,
): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: `/nx/cart/${queryStringEncode({ action })}`,
  })

export const urlResolverGetSearchPage = (
  config: PublicGlobalConfig,
  options?: {
    queryParams: {
      [key: string]: unknown
      // https://wiki.shop-apotheke.com/display/~kPitas/Important+Search+Tracking+Infos
      b?: string
      i?: string
      q?: string
      query?: string
      searchChannel?: string
      src?: string
      userToken?: string
    }
  },
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/search.htm${queryStringEncode(options?.queryParams)}`,
  })
}

export const urlResolverGetPathForPimProduct = (
  config: PublicGlobalConfig,
  pimProduct: PimProductV11,
): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: urlGetPathName(pimProduct.canonical_url),
  })

export const urlResolverGetMyTherapyApp = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/medikamenten-app/`,
  })
}

export const urlResolverGetMyTherapyAppDeepLink = (): string => {
  return 'https://activate.mytherapy.app/mmeT?deep_link_value=mytherapy%3A%2F%2Ftoday&af_dp=mytherapy%3A%2F%2Ftoday&af_xp=custom&pid=SAE-LP&c=MyT_REF_SAE-BAN_DE_00_None_All_PCLP&af_android_store_csl=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Deu.smartpatient.mytherapy%26listing%3Dmytherapy-for-shopapotheke&af_ios_store_cpp=8d6e14bd-5a7c-4954-9da6-8b39a4b87a81'
}

export const urlResolverGetInteractions = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/wechselwirkungen/`,
  })
}

export const urlResolverGetCounselor = (config: PublicGlobalConfig): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/ratgeber/`,
  })
}

export const urlResolverGetMedicineLexicon = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/medikamenten-lexikon/`,
  })
}

export const urlResolverGetContactMailPharmacy = (
  config: PublicGlobalConfig,
  options?: { queryParams: { openContactForm: boolean } },
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/versandapotheke_kontakt.htm${queryStringEncode(options?.queryParams)}`,
  })
}

export const urlResolverGetPharmaLandingPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/pharmazeutische-beratung/`,
  })
}

/**
 * link to page where user can redeem his prescription
 */
export const urlResolverGetRedeemPrescription = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/rezept-online-einloesen/`,
  })
}

export const urlResolverGetEprescriptionInfo = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/magazin/e-health/das-e-rezept/',
  })
}

export const urlResolverGetRedeemEprescription = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({ locale: config.locale, uri: `/e-rezept-einloesen/` })
}

export const urlResolverGetRedeemPaperPrescription = (
  config: PublicGlobalConfig,
): string => {
  if (
    config.tenant === TENANT.CH &&
    config.language === SupportedLanguages.FR
  ) {
    return urlNormalizeUri({ locale: LOCALE.FR_CH, uri: `/ordonnance/` })
  }

  if (
    config.tenant === TENANT.CH &&
    config.language === SupportedLanguages.DE
  ) {
    return urlNormalizeUri({ locale: LOCALE.DE_CH, uri: `/rezept-einloesen/` })
  }

  return urlNormalizeUri({ locale: config.locale, uri: `/rezept-einloesen/` })
}

export const urlResolverGetRedeemPaperPrescriptionConfirmation = (
  config: PublicGlobalConfig,
): string => {
  const { locale } = config

  const uriMap: Partial<Record<LOCALE, string>> = {
    [LOCALE.DE_CH]: '/rezept-einloesen/bestaetigung',
    [LOCALE.FR_CH]: '/ordonnance/confirmation',
  }

  const uri = uriMap[locale]

  return urlNormalizeUri({ locale, uri })
}

export const urlResolverGetMediServiceRxLandingPagePrivacyPolicy = (
  config: PublicGlobalConfig,
): string => {
  return config.locale === LOCALE.FR_CH
    ? 'https://www.mediservice.ch/fr/cookie-policy.php'
    : 'https://www.mediservice.ch/de/cookie-policy.php'
}

export const urlResolverGetMediServiceRxLandingPageFirstQuestionPartnerLink = (
  config: PublicGlobalConfig,
): string => {
  return config.locale === LOCALE.FR_CH
    ? 'https://www.mediservice.ch/fr/services/homecare.php'
    : 'https://www.mediservice.ch/de/services/homecare.php'
}

export const urlResolverGetMediServiceRxLandingPageSecondQuestionPartnerLink = (
  config: PublicGlobalConfig,
): string => {
  return config.locale === LOCALE.FR_CH
    ? 'https://www.mediservice.ch/mediserviceAssets/bin/alle-formulare-de-fr-it/rezeptbegleitkarten/redcare/240117-ch-fr-rezeptbegleitkarte-mediservice-a4-v4.pdf'
    : 'https://www.mediservice.ch/mediserviceAssets/bin/alle-formulare-de-fr-it/rezeptbegleitkarten/redcare/240117-ch-de-rezeptbegleitkarte-mediservice-a4-v3.pdf'
}

export const urlResolverGetRedPointsRoot = (
  config: PublicGlobalConfig,
): string => {
  if (config.tenant === TENANT.CH) {
    return urlNormalizeUri({ locale: config.locale, uri: '/redcoins/' })
  }

  return urlNormalizeUri({ locale: config.locale, uri: '/redpoints/' })
}

export const urlResolverGetRedPointsNx = (
  config: PublicGlobalConfig,
): string => {
  if (config.tenant === TENANT.CH) {
    return urlNormalizeUri({ locale: config.locale, uri: '/nx/redcoins/' })
  }

  return urlNormalizeUri({ locale: config.locale, uri: '/nx/redpoints/' })
}

export const urlResolverScanEprescription = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({ locale: config.locale, uri: `/e-rezept-scannen/` })
}

export const urlResolverNfcLandingPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/e-rezept-elektronische-gesundheitskarte/`,
  })
}

export const urlResolverErxDiscountConditionsPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/rabattbedingungen/`,
  })
}

export const urlResolverGetErxFamilyLandingPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/e-rezept-familie/`,
  })
}

export const urlResolverGetRepeatedPrescriptionFeedbackLandingPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/folgerezept/`,
  })
}

export const urlResolverGetOfflineOrderLoginPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/nx/digitale-dokumente/`,
  })
}

export const urlResolverGetOfflineOrderPage = (
  config: PublicGlobalConfig,
  orderIdAx: string,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/nx/digitale-dokumente/${orderIdAx}`,
  })
}

export const urlResolverGetFreeEnvelopePdf = (): string => {
  return 'https://static.shop-apotheke.com/media/pdf/freiumschlag.pdf'
}

export const urlResolverGetFreeEaEnvelopePdf = (): string => {
  return 'https://static.shop-apotheke.com/media/pdf/Freiumschlag-EAV.pdf'
}

export const urlResolverGetRxFreeEnvelopePdf = (): string => {
  return 'https://www.mediservice.ch/mediserviceAssets/bin/alle-formulare-de-fr-it/gas/mediservice_adressetikett.pdf'
}

export const urlResolverGetShopApothekeApp = (
  config: PublicGlobalConfig,
): string =>
  urlNormalizeUri({ locale: config.locale, uri: '/lp/shop-apotheke-app' })

/**
 * One Link to eRx flow at Shopapotheke App
 */
export const urlResolverGoToNfcAtShopApothekeApp = (): string =>
  'https://shop-apotheke-com.onelink.me/d95Q?pid=Onsite&c=Onsite_DE_Erx_LP_NFC_Button&af_dp=sae://erx&deep_link_value=sae://erx&af_web_dp=https://www.shop-apotheke.com/lp/shop-apotheke-app'

export const urlResolverGetPrivacySecurity = (
  config: PublicGlobalConfig,
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/datenschutz.htm',
    [LOCALE.DE_CH]: '/datenschutz.htm',
    [LOCALE.DE_DE]: '/datenschutz.htm',
    [LOCALE.FR_BE]: '/vie-privee.htm',
    [LOCALE.FR_CH]: '/protection-des-donnees.htm',
    [LOCALE.FR_FR]: '/cgu.htm',
    [LOCALE.IT_IT]: '/politica-protezione-dati-personali.htm',
    [LOCALE.NL_BE]: '/privacy-beleid.htm',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

export const urlResolverGetPrivacySecurityCreditCheck = (
  config: PublicGlobalConfig,
): string => {
  const localeMap: Partial<Record<LOCALE, string>> = {
    [LOCALE.DE_AT]: '/datenschutz.htm#bonitaet',
    [LOCALE.DE_DE]: '/datenschutz.htm#bonitaet',
    [LOCALE.FR_BE]: '/protection-des-donnees.htm',
    [LOCALE.FR_FR]: '/protection-des-donnees.htm',
    [LOCALE.IT_IT]: '/politica-protezione-dati-personali.htm',
    [LOCALE.NL_BE]: '/gegevensbescherming.htm#bonitaet',
  }

  const uri = localeMap[config.locale]
  if (!uri) {
    apmCaptureError(
      new Error(
        `Missing locale mapping for ${config.locale} in urlResolverGetPrivacySecurityCreditCheck`,
      ),
    )

    return ''
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri,
  })
}

export const urlResolverGetAgb = (config: PublicGlobalConfig): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/versandapotheke_agb.htm',
    [LOCALE.DE_CH]: '/versandapotheke_agb.htm',
    [LOCALE.DE_DE]: '/versandapotheke_agb.htm',
    [LOCALE.FR_BE]: '/cga.htm',
    [LOCALE.FR_CH]: '/cga.htm',
    [LOCALE.FR_FR]: '/cgv.htm',
    [LOCALE.IT_IT]: '/cgv.htm',
    [LOCALE.NL_BE]: '/algemene-voorwaarden.htm',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

export const urlResolverGetLinkToAllProductsOfSeller = (
  config: PublicGlobalConfig,
  sellerId: string,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/nx/partnerproducts/id-${sellerId}/`,
  })
}

export const urlResolverGetAccountability = (
  config: PublicGlobalConfig,
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/apotheke_haftung.htm',
    [LOCALE.DE_CH]: '/allgemeine-nutzungsbedingungen.htm',
    [LOCALE.DE_DE]: '/apotheke_haftung.htm',
    [LOCALE.FR_BE]: '/cgu.htm',
    [LOCALE.FR_CH]: '/cgu.htm',
    [LOCALE.FR_FR]: '/cgu.htm',
    [LOCALE.IT_IT]: '/cgu.htm',
    [LOCALE.NL_BE]: '/algemene-gebruiksvoorwaarden.htm',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

export const urlResolverGetImprint = (config: PublicGlobalConfig): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/apotheke_impressum.htm',
    [LOCALE.DE_CH]: '/apotheke_impressum.htm',
    [LOCALE.DE_DE]: '/apotheke_impressum.htm',
    [LOCALE.FR_BE]: '/mentions-legales.htm',
    [LOCALE.FR_CH]: '/apotheke_impressum.htm',
    [LOCALE.FR_FR]: '/apotheke_impressum.htm',
    [LOCALE.IT_IT]: '/note-legali.htm',
    [LOCALE.NL_BE]: '/wettelijke-informatie.htm',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

/**
 * Generates the URL to use to share the product on facebook, depending on the device
 */
export const urlResolverGetFacebookProductShareUrl = (
  product: PimProductV11,
  deviceType: string,
): string => {
  return `https://www.facebook.com/sharer/sharer.php${queryStringEncode({
    // eslint-disable-next-line id-length -- the identifier name is too short, and it can't be changed
    u: `${product.canonical_url}?campaign=share/facebook/${deviceType}/${product.upid}`,
  })}`
}

/**
 * Generates the URL to use to send the product using mail
 */
export const urlResolverGetSendProductInEmailUrl = (
  subject: string,
): string => {
  return `mailto:?subject=${subject}`
}

/**
 * Generates the URL to use to share the product on Pinterest, depending on the device
 */
export const urlResolverGetPinterestProductShareUrl = (
  product: PimProductV11,
  deviceType: string,
): string => {
  return `https://www.pinterest.com/pin/create/link/${queryStringEncode({
    description: product.name,
    url: `${product.canonical_url}?campaign=share/pinterest/${deviceType}/${product.upid}`,
  })}`
}

/**
 * Generates the URL to use to share the product on Twitter, depending on the device
 */
export const urlResolverGetTwitterProductShareUrl = (
  product: PimProductV11,
  deviceType: string,
): string => {
  return `https://twitter.com/intent/tweet${queryStringEncode({
    text: product.name,
    url: `${product.canonical_url}?campaign=share/twitter/${deviceType}/${product.upid}`,
  })}`
}

/**
 * Generates the URL to use to share the product on Whatsapp, depending on the device
 */
export const urlResolverGetWhatsappProductShareUrl = (
  product: PimProductV11,
  deviceType: string,
): string => {
  return `whatsapp://send${queryStringEncode({
    text: product.name,
    url: `${product.canonical_url}?campaign=share/whatsapp/${deviceType}/${product.upid}`,
  })}`
}

/**
 * Generates the URL of the product to be copied, depending on the device
 */
export const urlResolverGetProductShareUrl = (
  product: PimProductV11,
  deviceType: string,
): string => {
  return `${product.canonical_url}?campaign=share/copylink/${deviceType}/${product.upid}`
}

/**
 * cart intermediate page handles adding item to cart.
 */
export const urlResolverGetCartIntermediatePage = (
  config: PublicGlobalConfig,
  params?: ProductAddToCartCswCtaParams | ProductAddToCartCtaParams,
): string => {
  const base = urlNormalizeUri({
    locale: config.locale,
    uri: `/nx/cartintermediate/`,
  })

  if (params) {
    return `${base}${queryStringEncode(
      params as unknown as Record<string, unknown>,
    )}`
  }

  return base
}

/**
 * Ods transition url, currently end url will be handled by fock.
 * @example:
 * {target: 'zava.com'} -> /transition?target=zava.com
 */
export const urlResolverGetOdsTransition = (params: {
  target: string
}): string => {
  return `/transition${queryStringEncode(params)}`
}

export const urlResolverGetLoginApiEndpoint = (): string => {
  return `${PUBLIC_API_PREFIX}/api/login`
}

export const urlResolverGetUserRegisterApiEndpoint = (): string => {
  return `${PUBLIC_API_PREFIX}/api/register/`
}

export const urlResolverGetOfflineOrderUserAuthenticationApiEndpoint =
  (): string => {
    return `${PUBLIC_API_PREFIX}/api/offline-orders/authenticate`
  }

export const urlResolverGetOneTimePasswordInitiateApiEndpoint = (
  config: PublicGlobalConfig,
): string => {
  return `${PUBLIC_API_PREFIX}/api/bully/auth/v3/${config.tenantAndEnv}/login/otp/initiate`
}

export const urlResolverGetOneTimePasswordConfirmApiEndpoint = (
  config: PublicGlobalConfig,
): string => {
  return `${PUBLIC_API_PREFIX}/api/bully/auth/v3/${config.tenantAndEnv}/login/otp/confirm`
}

export const urlResolverGetResetPasswordApiEndpoint = (): string => {
  return `${PUBLIC_API_PREFIX}/api/reset-password`
}

export const urlResolverGetCrossSellHorizontalSliderApiEndpoint =
  (): string => {
    return `${PUBLIC_API_PREFIX}/api/cross-sell/horizontal-slider`
  }

export const urlResolverGetOdsVideoConsultation = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/online-arzt-service/videosprechstunde/',
  })
}

export const urlResolverGetAccountNewsletter = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/nx/account/newsletter/',
  })
}

export const urlResolverGetAccountChangeEmail = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/nx/account/change-email/',
  })
}

export const urlResolverGetNewsletter = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({ locale: config.locale, uri: '/nx/newsletter/' })
}

export const urlResolverGetNewsletterConfirmation = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/nx/newsletter/confirmation',
  })
}

export const urlResolverGetNewsletterSubscribeUnsubscribe = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/nx/newsletter/unsubscribe',
  })
}

export const urlResolverGetFaq = (
  config: PublicGlobalConfig,
  { hash = '' }: { hash?: string } = {},
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/faq/${hash}`,
  })
}

export const urlResolverGetFaqProductReviews = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: urlResolverGetFaq(config, { hash: '#faq-product-4' }),
  })
}

export const urlResolverGetFaqPrescriptions = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: urlResolverGetFaq(config, { hash: '#f05' }),
  })
}

export const urlResolverGetOffersCategoryPage = (
  config: PublicGlobalConfig,
): string => {
  switch (config.locale) {
    case LOCALE.DE_CH: {
      return urlNormalizeUri({
        locale: config.locale,
        uri: '/aktionen',
      })
    }

    case LOCALE.FR_BE: {
      return urlNormalizeUri({
        locale: config.locale,
        uri: '/offres-speciales-fr',
      })
    }

    case LOCALE.FR_CH: {
      return urlNormalizeUri({
        locale: config.locale,
        uri: '/bons-plans',
      })
    }

    case LOCALE.FR_FR: {
      return urlNormalizeUri({
        locale: config.locale,
        uri: '/vente-flash',
      })
    }

    case LOCALE.IT_IT: {
      return urlNormalizeUri({
        locale: config.locale,
        uri: '/promozioni-set',
      })
    }

    case LOCALE.NL_BE: {
      return urlNormalizeUri({
        locale: config.locale,
        uri: '/speciale-aanbiedingen-nl',
      })
    }

    default: {
      return urlNormalizeUri({
        locale: config.locale,
        uri: '/angebote',
      })
    }
  }
}

export const urlResolverGetFaqHowReviewScoreCalculated = (
  config: PublicGlobalConfig,
): string => {
  if (config.tenant === TENANT.IT) {
    return urlNormalizeUri({
      locale: config.locale,
      uri: urlResolverGetFaq(config, { hash: '#faq-product-3-h' }),
    })
  }

  // same for FR CH DE
  return urlNormalizeUri({
    locale: config.locale,
    uri: urlResolverGetFaq(config, { hash: '#faq-product-3' }),
  })
}

export const urlResolverGetFaqSellerEvaluations = (
  config: PublicGlobalConfig,
): string => {
  if (config.tenant === TENANT.AT) {
    return urlNormalizeUri({
      locale: config.locale,
      uri: urlResolverGetFaq(config, { hash: '#frage-8-22' }),
    })
  }

  // COM
  return urlNormalizeUri({
    locale: config.locale,
    uri: urlResolverGetFaq(config, { hash: '#frage-8-23' }),
  })
}

enum CONTACT_PAGE_FAQ_QUESTION_CARDS {
  CANCEL_ORDER = 'cancelOrder',
  DELIVERY_INFO = 'deliveryInfo',
  PAYMENT_METHODS = 'paymentMethods',
  REDEEM_VOUCHER = 'redeemVoucher',
  RETURN_ORDER = 'returnOrder',
  SHIPPING_COST = 'shippingCost',
}

// italy is not ready

const FAQ_ITEM_MAP: Record<
  CONTACT_PAGE_FAQ_QUESTION_CARDS,
  Record<TENANT, string>
> = {
  cancelOrder: {
    [TENANT.AT]: '#frage-0-8',
    [TENANT.BE]: '#frage-2-11',
    [TENANT.CH]: '#frage-0-8',
    [TENANT.COM]: '#frage-0-1',
    [TENANT.FR]: '#frage-0-13',
    [TENANT.IT]: '#frage-0-11',
  },
  deliveryInfo: {
    [TENANT.AT]: '#frage-1-1',
    [TENANT.BE]: '#frage-4-1',
    [TENANT.CH]: '#frage-1-1',
    [TENANT.COM]: '#frage-1-1',
    [TENANT.FR]: '#frage-1-1',
    [TENANT.IT]: '#frage-1-0',
  },
  paymentMethods: {
    [TENANT.AT]: '#frage-2-0',
    [TENANT.BE]: '#frage-3-1',
    [TENANT.CH]: '#frage-2-0',
    [TENANT.COM]: '#frage-2-0',
    [TENANT.FR]: '#frage-2-0',
    [TENANT.IT]: '#frage-2-0',
  },
  redeemVoucher: {
    [TENANT.AT]: '#frage-0-6',
    [TENANT.BE]: '#frage-2-3',
    [TENANT.CH]: '#frage-0-6',
    [TENANT.COM]: '#frage-0-7',
    [TENANT.FR]: '#frage-0-7',
    [TENANT.IT]: '#frage-0-7',
  },
  returnOrder: {
    [TENANT.AT]: '#frage-0-3',
    [TENANT.BE]: '#frage-6-0',
    [TENANT.CH]: '#frage-0-3',
    [TENANT.COM]: '#frage-0-5',
    [TENANT.FR]: '#frage-0-11',
    [TENANT.IT]: '#frage-0-8',
  },
  shippingCost: {
    [TENANT.AT]: '#frage-1-2',
    [TENANT.BE]: '#frage-4-0',
    [TENANT.CH]: '#frage-1-2',
    [TENANT.COM]: '#frage-1-2',
    [TENANT.FR]: '#frage-1-2',
    [TENANT.IT]: '#frage-1-2',
  },
}

const getFaqItemUrl = (
  config: PublicGlobalConfig,
  itemType: CONTACT_PAGE_FAQ_QUESTION_CARDS,
): string => {
  const itemMap = FAQ_ITEM_MAP[itemType]

  return urlNormalizeUri({
    locale: config.locale,
    uri: urlResolverGetFaq(config, { hash: itemMap[config.tenant] }),
  })
}

export const urlResolverGetFaqItemCancelOrder = (
  config: PublicGlobalConfig,
): string => getFaqItemUrl(config, CONTACT_PAGE_FAQ_QUESTION_CARDS.CANCEL_ORDER)

export const urlResolverGetFaqItemDeliveryInfo = (
  config: PublicGlobalConfig,
): string =>
  getFaqItemUrl(config, CONTACT_PAGE_FAQ_QUESTION_CARDS.DELIVERY_INFO)

export const urlResolverGetFaqItemReturnOrder = (
  config: PublicGlobalConfig,
): string => getFaqItemUrl(config, CONTACT_PAGE_FAQ_QUESTION_CARDS.RETURN_ORDER)

export const urlResolverGetFaqItemRedeemVoucher = (
  config: PublicGlobalConfig,
): string =>
  getFaqItemUrl(config, CONTACT_PAGE_FAQ_QUESTION_CARDS.REDEEM_VOUCHER)

export const urlResolverGetFaqitemShippingCost = (
  config: PublicGlobalConfig,
): string =>
  getFaqItemUrl(config, CONTACT_PAGE_FAQ_QUESTION_CARDS.SHIPPING_COST)

export const urlResolverGetFaqItemPaymentMethods = (
  config: PublicGlobalConfig,
): string =>
  getFaqItemUrl(config, CONTACT_PAGE_FAQ_QUESTION_CARDS.PAYMENT_METHODS)

export const urlResolverGetFaqReviewsCriteria = (
  config: PublicGlobalConfig,
): string => {
  if (config.tenant === TENANT.IT) {
    return urlNormalizeUri({
      locale: config.locale,
      uri: urlResolverGetFaq(config, { hash: '#faq-product-1-h' }),
    })
  }

  // same for FR CH DE
  return urlNormalizeUri({
    locale: config.locale,
    uri: urlResolverGetFaq(config, { hash: '#faq-product-1' }),
  })
}

export const urlResolverGetChangePassword = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}change-password/`,
  })
}

export const urlResolverGetResetPassword = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}reset-password/`,
  })
}
export const urlResolverGetBillingAddress = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}billing-address`,
  })
}
export const urlResolverGetShippingAddress = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}shipping-address`,
  })
}

export const urlResolverGetEavCatalogue = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}eav-catalogue`,
  })
}

export const urlResolverGetChangeDateOfBirth = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}change-date-of-birth`,
  })
}
export const urlResolverGetOnlineArztService = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/online-arzt-service.htm',
  })
}

export const urlResolverGetMedicationPlan = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}medication-plan/`,
  })
}

export const urlResolverGetCopaymentAnnualReport = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}copayment-report`,
  })
}

const urlResolverGetAccountMessages = (config: PublicGlobalConfig): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccount(config)}messages`,
  })
}

export const urlResolverGetMessagesOverview = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccountMessages(config)}overview`,
  })
}

export const urlResolverGetMessagesFromThreadId = (
  config: PublicGlobalConfig,
  threadId: string,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccountMessages(config)}thread/${threadId}`,
  })
}

export const urlResolverGetThreadIdAttachment = (
  config: PublicGlobalConfig,
  threadId: string,
  attachmentId: string,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccountMessages(config)}thread/${threadId}/${attachmentId}`,
  })
}

export const urlResolverGetRezeptbonus = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({ locale: config.locale, uri: '/rezeptbonus/' })
}

export const urlResolverGetRezeptbonusLandingPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({ locale: config.locale, uri: '/lp/rezeptbonus/' })
}

export const urlResolverGetSmartExpert = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({ locale: config.locale, uri: '/smart/' })
}

export const urlResolverGetAllergy = (config: PublicGlobalConfig): string => {
  return urlNormalizeUri({ locale: config.locale, uri: '/allergie/' })
}

export const urlResolverGetColds = (config: PublicGlobalConfig): string => {
  return urlNormalizeUri({ locale: config.locale, uri: '/erkaeltung/' })
}

export const urlResolverGetVitamin = (config: PublicGlobalConfig): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: '/vitamine-mineralstoffe-enzyme/',
  })
}

export const urlResolverMyTherapy = (): string => {
  return 'https://activate.mytherapyapp.com/746744'
}

const redcareBrandPages: Partial<Record<LOCALE, string>> = {
  [LOCALE.DE_AT]: '/markenshops/redcare/',
  [LOCALE.DE_CH]: '/markenshops/redcare/',
  [LOCALE.FR_BE]: '/marques/redcare/',
  [LOCALE.FR_CH]: '/espace-marque/redcare/',
  [LOCALE.NL_BE]: '/merken/redcare/',
}

export const urlResolverRedCare = (config: PublicGlobalConfig): string => {
  const uri = redcareBrandPages[config.locale] ?? '/search.htm?b=1&q=redcare'

  return urlNormalizeUri({
    locale: config.locale,
    uri,
  })
}

export const urlResolverGetVouchersPage = (
  config: PublicGlobalConfig,
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/lp/gutscheine/',
    [LOCALE.DE_CH]: '/lp/gutscheine/',
    [LOCALE.DE_DE]: '/lp/gutscheine/',
    [LOCALE.FR_BE]: '/lp/code-promo/',
    [LOCALE.FR_CH]: '/lp/code-promo/',
    [LOCALE.FR_FR]: '/lp/code-promo/',
    [LOCALE.IT_IT]: '/lp/buoni/',
    [LOCALE.NL_BE]: '/lp/promocodes/',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

export const urlResolverGetAppLandingPage = (
  config: PublicGlobalConfig,
  options?: {
    queryParams: {
      src?: string
    }
  },
): string => {
  const localeMap: Partial<Record<LOCALE, string>> = {
    [LOCALE.DE_AT]: '/lp/shop-apotheke-app/',
    [LOCALE.DE_CH]: '/lp/redcare-apotheke-app/',
    [LOCALE.DE_DE]: '/lp/shop-apotheke-app/',
    [LOCALE.FR_CH]: '/lp/redcare-apotheke-app/',
    [LOCALE.FR_FR]: '/lp/appli-redcare-pharmacie/',
    [LOCALE.IT_IT]: '/lp/redcare-app/',
  }

  const uri = localeMap[config.locale]
  if (!uri) {
    apmCaptureError(
      new Error(
        `Missing locale mapping for ${config.locale} in urlResolverGetAppLandingPage`,
      ),
    )

    return ''
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: `${uri}${queryStringEncode(options?.queryParams)}`,
  })
}

export const urlResolverGetOwnBrandsLandingPage = (
  config: PublicGlobalConfig,
): string => {
  const localeMap: Partial<Record<LOCALE, string>> = {
    [LOCALE.DE_AT]: '/produkte-redcare/',
    [LOCALE.DE_CH]: '/sale-6/',
    [LOCALE.DE_DE]: '/healthydeals-redcare/',
    [LOCALE.FR_CH]: '/soldes-6/',
    [LOCALE.FR_FR]: '/specials10/',
    [LOCALE.IT_IT]: '/speciale-13/',
  }

  const uri = localeMap[config.locale]
  if (!uri) {
    apmCaptureError(
      new Error(
        `Missing locale mapping for ${config.locale} in urlResolverGetOwnBrandsLandingPage`,
      ),
    )

    return ''
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri,
  })
}

export const urlResolverGetNewsletterPage = (
  config: PublicGlobalConfig,
  options?: {
    queryParams: {
      src?: string
    }
  },
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `/nx/newsletter/${queryStringEncode(options?.queryParams)}`,
  })
}

export const urlResolverGetContactUs = (
  config: Pick<PublicGlobalConfig, 'locale'>,
  options?: {
    queryParams: {
      target?:
        | 'account-help-others'
        | 'account-help'
        | 'cancellation'
        | 'change-order'
        | 'damaged-product'
        | 'delete-account'
        | 'document-missing'
        | 'document-status'
        | 'footer'
        | 'header'
        | 'missing-product'
        | 'my-account'
        | 'order-details-other-topics'
        | 'order-question'
        | 'order-tracking'
        | 'other-topics'
        | 'payment-information'
        | 'pharmaceutical'
        | 'return-article'
        | 'search-page'
    }
  },
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/versandapotheke_kontakt.htm',
    [LOCALE.DE_CH]: '/versandapotheke_kontakt.htm',
    [LOCALE.DE_DE]: '/versandapotheke_kontakt.htm',
    [LOCALE.FR_BE]: '/contactez_nous.htm',
    [LOCALE.FR_CH]: '/contact.htm',
    [LOCALE.FR_FR]: '/contact.htm',
    [LOCALE.IT_IT]: '/contatti.htm',
    [LOCALE.NL_BE]: '/contacteer_ons.htm',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: `${localeMap[config.locale]}${queryStringEncode(options?.queryParams)}`,
  })
}

const aboutUsLocaleMap = {
  [LOCALE.DE_AT]: '/about/',
  [LOCALE.DE_CH]: '/about/',
  [LOCALE.DE_DE]: '/about/',
  [LOCALE.FR_BE]: '/a-propos-de-farmaline/',
  [LOCALE.FR_CH]: '/qui-sommes-nous/',
  [LOCALE.FR_FR]: '/qui-sommes-nous/',
  [LOCALE.IT_IT]: '/su-di-noi/',
  [LOCALE.NL_BE]: '/over-farmaline/',
}

export const urlResolverGetAboutUsPage = (config: PublicGlobalConfig): string =>
  urlNormalizeUri({
    locale: config.locale,
    uri: aboutUsLocaleMap[config.locale],
  })

export const urlResolverGetLeafletSearchPage = (): string => {
  return urlNormalizeUri({
    uri: `/beipackzettel`,
  })
}

export const urlResolverGetLeafletSearchResultsPage = (
  searchQuery: string,
): string => {
  return urlNormalizeUri({
    uri: `/beipackzettel/suchergebnisse${queryStringEncode({
      // eslint-disable-next-line id-length -- search parameter
      q: searchQuery,
    })}`,
  })
}

export const urlResolverGetCheckoutAddress = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    uri: `/nx/checkout/${config.tenantAndEnv}/${config.language}/address`,
  })
}

export const urlResolverGetInteractionsInfo = (): string => {
  return urlNormalizeUri({
    uri: '/magazin/e-health/e-services/der-wechselwirkungscheck/',
  })
}

export const urlResolverGetOrderQuestion = (
  config: PublicGlobalConfig,
  invoicesSection?: boolean,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetAccount(config)}fragen-bestellung/${queryStringEncode({ invoicesSection })}`,
  })
}

export const urlResolverGetOrderQuestionMixedOrderSubOrderPickerPage = (
  config: PublicGlobalConfig,
  orderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestion(config)}${orderId}`,
  })
}

export const urlResolverGetOrderQuestionSubOrderDetailPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionMixedOrderSubOrderPickerPage(config, orderId)}${subOrderId}`,
  })
}

export const urlResolverGetOtherTopicsContactPage = (
  config: PublicGlobalConfig,
  source?: string,
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/sonstige-beratung/',
    [LOCALE.DE_CH]: '/sonstige-beratung/',
    [LOCALE.DE_DE]: '/sonstige-beratung/',
    [LOCALE.FR_BE]: `/other-questions/`,
    [LOCALE.FR_CH]: `/other-questions/`,
    [LOCALE.FR_FR]: `/other-questions/`,
    [LOCALE.IT_IT]: `/other-questions/`,
    [LOCALE.NL_BE]: `/other-questions/`,
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: `${localeMap[config.locale]}${queryStringEncode({ source })}`,
  })
}

export const urlResolverGetHelpCentrePharmaPage = (
  config: PublicGlobalConfig,
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/pharmazeutische-fragen/',
    [LOCALE.DE_CH]: '/pharmazeutische-fragen/',
    [LOCALE.DE_DE]: '/pharmazeutische-fragen/',
    [LOCALE.FR_BE]: '/pharmaceutical-questions/',
    [LOCALE.FR_CH]: '/pharmaceutical-questions/',
    [LOCALE.FR_FR]: '/pharmaceutical-questions/',
    [LOCALE.IT_IT]: '/pharmaceutical-questions/',
    [LOCALE.NL_BE]: '/pharmaceutical-questions/',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

export const urlResolverGetOrderQuestionsReportDamagedProductPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}damaged-product-contact`,
  })
}
export const urlResolverGetOrderQuestionsCancellablePage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}cancellable`,
  })
}

export const urlResolverGetOrderQuestionsReturnItemPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}return-item`,
  })
}

export const urlResolverGetOrderQuestionsTrackingPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}tracking`,
  })
}

export const urlResolverGetOrderQuestionsPaymentPage = (options: {
  config: PublicGlobalConfig
  orderId: string
  params?: {
    paymentType?: string
  }
  subOrderId: string
}): string => {
  const { config, orderId, params, subOrderId } = options

  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}payment${queryStringEncode({ paymentType: params?.paymentType })}`,
  })
}

export const urlResolverGetOrderQuestionsPaymentReminderPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionsPaymentPage({ config, orderId, subOrderId })}reminder`,
  })
}

export const urlResolverGetOrderQuestionsPaymentDeferralPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionsPaymentPage({ config, orderId, subOrderId })}deferral`,
  })
}

export const urlResolverGetOrderQuestionsMarketPlaceContactForm = (
  config: PublicGlobalConfig,
  options: {
    contactSubject: string
    isAmazonization?: boolean
    orderId: string
    subOrderId: string
  },
): string => {
  const searchParams = new URLSearchParams()
  searchParams.set('contactSubject', options.contactSubject)

  if (options.isAmazonization) {
    searchParams.set('target', 'amazonization')
  }

  return urlNormalizeUri({
    uri: `${urlResolverGetAccountSubOrderDetailBySubOrderId(config, options.orderId, options.subOrderId)}?${searchParams.toString()}`,
  })
}

export const urlResolverGetProductsCancellationPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetAccountSubOrderDetailBySubOrderId(config, orderId, subOrderId)}products-cancellation`,
  })
}

export const urlResolverGetReportOrderIncident = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetAccount(config)}incident/${orderId}/${subOrderId}`,
  })
}

export const urlResolverGetRefundOrder = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetAccount(config)}refund/${orderId}/${subOrderId}`,
  })
}

export const urlResolverGetOrderQuestionsOrderChangePage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}order-change`,
  })
}

export const urlResolverGetRatepayOrderBuyerPortalUrl = (
  paymentReference: string,
): string => {
  return `https://portal.ratepay.com/${paymentReference}`
}

export const urlResolverGetOrderQuestionsDocumentsPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}documents`,
  })
}

export const urlResolverGetOrderQuestionsMissingProductsPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionSubOrderDetailPage(config, orderId, subOrderId)}missing-products`,
  })
}

export const urlResolverGetOrderQuestionsDocumentsMissingPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionsDocumentsPage(config, orderId, subOrderId)}missing`,
  })
}

export const urlResolverGetOrderQuestionsDocumentsStatusPage = (
  config: PublicGlobalConfig,
  orderId: string,
  subOrderId: string,
): string => {
  return urlNormalizeUri({
    uri: `${urlResolverGetOrderQuestionsDocumentsPage(config, orderId, subOrderId)}status`,
  })
}

export const urlResolverGetAccountChangePage = (
  config: PublicGlobalConfig,
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/fragen-kundenkonto',
    [LOCALE.DE_CH]: '/fragen-kundenkonto',
    [LOCALE.DE_DE]: '/fragen-kundenkonto',
    [LOCALE.FR_BE]: '/account-help',
    [LOCALE.FR_CH]: '/account-help',
    [LOCALE.FR_FR]: '/account-help',
    [LOCALE.IT_IT]: '/account-help',
    [LOCALE.NL_BE]: '/account-help',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

export const urlResolverGetAccountChangeNamePage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccountChangePage(config)}change-name`,
  })
}
export const urlResolverGetAccountDeleteAccountPage = (
  config: PublicGlobalConfig,
): string => {
  return urlNormalizeUri({
    locale: config.locale,
    uri: `${urlResolverGetAccountChangePage(config)}delete-account`,
  })
}

export const urlResolverGetBrandsIndexPage = (
  config: PublicGlobalConfig,
): string => {
  const localeMap = {
    [LOCALE.DE_AT]: '/marken/',
    [LOCALE.DE_CH]: '/marken/',
    [LOCALE.DE_DE]: '/marken/',
    [LOCALE.FR_BE]: '/marques/',
    [LOCALE.FR_CH]: '/marques/',
    [LOCALE.FR_FR]: '/marques/',
    [LOCALE.IT_IT]: '/marchi/',
    [LOCALE.NL_BE]: '/merken/',
  }

  return urlNormalizeUri({
    locale: config.locale,
    uri: localeMap[config.locale],
  })
}

export const urlResolverGetFaqRatingGuidelines = (
  config: PublicGlobalConfig,
): string => {
  const localeMap: Record<LOCALE, string> = {
    [LOCALE.DE_AT]: '#frage-6-3',
    [LOCALE.DE_CH]: '#frage-5-4',
    [LOCALE.DE_DE]: '#frage-7-4',
    [LOCALE.FR_BE]: '',
    [LOCALE.FR_CH]: '#frage-5-4',
    [LOCALE.FR_FR]: '#frage-6-3',
    [LOCALE.IT_IT]: '#frage-6-3',
    [LOCALE.NL_BE]: '',
  }

  return urlResolverGetFaq(config, {
    hash: localeMap[config.locale],
  })
}
